import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useAreas } from "@/features/deployment-dashboard/shared/hooks/use-areas";
import { useAreaMaps } from "@/features/deployment-dashboard/shared/hooks/use-area-maps";
import {
  floorplanImageQueryOptions,
  type FloorplanImageIdentity,
} from "@/features/deployment-dashboard/shared/floorplan-image-query";

interface FloorplanPreloadTarget {
  identity: FloorplanImageIdentity;
  sourceUrl: string;
}

/**
 * Headless background preloader for a selected deployment. Fetches every
 * area authorized by the current `areas_list` grant, then downloads each
 * distinct floorplan raster into the TanStack Query cache (and, transitively,
 * persistent Cache Storage) so Live View, Analytics, and other deployment
 * routes render without waiting on first navigation to a given area.
 *
 * Always renders `null`: preloading never blocks or gates route rendering,
 * and every area/image query is independent, so one failure (a missing
 * floorplan, a signing error, a decode failure, etc.) never cancels the
 * others. A failed image remains retryable simply by selecting that area,
 * since the selected-area consumer shares the same query/cache identity.
 */
export function DeploymentFloorplanPreloader({ deploymentId }: { deploymentId: string }) {
  const areasQuery = useAreas(deploymentId);
  // Only ever preload areas the current `areas_list` grant actually returned;
  // never infer, remember, or reuse area ids from a prior selection/deployment.
  const areaIds = useMemo(() => areasQuery.data?.map((area) => area.id) ?? [], [areasQuery.data]);

  // TODO(preload-bulk-endpoint): This fans out one GET /v1/areas/{areaId}/map
  // request per authorized area returned by GET /v1/areas (N+1 metadata
  // requests). Replace with a single bulk runtime API endpoint that returns
  // every authorized area's map metadata (including sensorIds/floorplan) in
  // one round trip once available.
  const areaMapQueries = useAreaMaps(deploymentId, areaIds);

  const floorplanTargets = useMemo<FloorplanPreloadTarget[]>(() => {
    const byIdentity = new Map<string, FloorplanPreloadTarget>();

    for (const query of areaMapQueries) {
      const floorplan = query.data?.floorplan;
      if (!floorplan) {
        continue;
      }

      // Deduplicate by (floorplanId, revision): areas sharing a raster
      // download it once, while each area's own map response (transform,
      // observation areas, sensorIds) is preserved untouched for Phase 3.
      const key = `${floorplan.floorplanId}@${floorplan.revision}`;
      if (!byIdentity.has(key)) {
        byIdentity.set(key, {
          identity: {
            deploymentId,
            floorplanId: floorplan.floorplanId,
            revision: floorplan.revision,
          },
          sourceUrl: floorplan.imageUrl,
        });
      }
    }

    return [...byIdentity.values()];
  }, [areaMapQueries, deploymentId]);

  // Independent per-floorplan queries: never `Promise.all`, so one failed
  // raster (expired signature, decode error, etc.) cannot cancel the rest.
  // `floorplanImageQueryOptions` never creates an object URL; only a mounted
  // schematic consumer does that.
  useQueries({
    queries: floorplanTargets.map(({ identity, sourceUrl }) =>
      floorplanImageQueryOptions(identity, sourceUrl)
    ),
  });

  return null;
}
